import type {
  ApiResponse,
  COMMON_STATUS,
  CommonParams,
  CreateUserDto,
  UserLessonDto,
  UserQuizDto,
  RoleEnum,
  UpdateUserDto,
  UpdateUserResponse,
  User,
  UserInStore,
  ECOMPANY_BUSINESS,
} from "@/types";
import api from "./api";

export interface UserServiceParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface UserDto {
  id: number;
  company_id: number;
  store_id: number;
  company: { id: number; name?: string; business: ECOMPANY_BUSINESS };
  store: { id: number; name?: string };
  role: { id: number; name: RoleEnum };
  groups?: { id: number; group_id: number }[];
  user_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  position?: string;
  department?: string;
  image?: string | null;
  full_name: string;
}

export const userService = {
  // Get all users
  getUsers: async (params: UserServiceParams = {}): Promise<User[]> => {
    try {
      const response = await api.get<User[]>("/users", { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to fetch users");
    }
  },

  // Get user by ID
  getUserById: async (id: number) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data.data;
    } catch (error: any) {
      throw error?.response?.data;
    }
  },

  getUser: async (): Promise<UserDto> => {
    try {
      const response = await api.get(`/users/me`);
      return response.data.data;
    } catch (error: any) {
      throw error?.response?.data;
    }
  },

  // Create new user
  createUser: async (
    userData: Partial<CreateUserDto>
  ): Promise<ApiResponse<User>> => {
    try {
      const response = await api.post<User>("/users", userData);
      return response;
    } catch (error: any) {
      throw error.response.data;
    }
  },

  // Update user
  updateUser: async (id: number, userData: Partial<User>): Promise<User> => {
    try {
      const response = await api.put<User>(`/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to update user");
    }
  },

  updateUserInfor: async (
    id: number,
    userData: Partial<UpdateUserDto>
  ): Promise<ApiResponse<UpdateUserResponse>> => {
    try {
      const response = await api.patch(`/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      throw error.response.data;
    }
  },

  resendInfor: async (id: number): Promise<ApiResponse<null>> => {
    try {
      const response = await api.post(`/users/${id}/resend`);
      return response.data;
    } catch (error: any) {
      throw error.response.data;
    }
  },

  changeAvatar: async (
    id: number,
    data: { file: File | null }
  ): Promise<ApiResponse<null>> => {
    try {
      const formData = new FormData();

      if (data.file) {
        formData.append("file", data.file);
      }

      const response = await api.post(`/users/${id}/image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      throw error.response.data;
    }
  },

  // Delete user
  deleteUser: async (id: number): Promise<ApiResponse<null>> => {
    try {
      const resp = await api.delete(`/users/${id}`);
      return resp;
    } catch (error: any) {
      throw error?.response?.data;
    }
  },

  async getMe() {
    const res = await api.get<ApiResponse<UserDto>>("/users/me");
    return res.data?.data;
  },

  getUsersInStore: async (
    params: CommonParams<{}>,
    id: string
  ): Promise<ApiResponse<UserInStore[]>> => {
    try {
      const response = await api.get(`/stores/${id}/users`, { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch users in store"
      );
    }
  },

  getMyUsers: async (
    params: CommonParams<{}>
  ): Promise<ApiResponse<UserInStore[]>> => {
    try {
      const response = await api.get(`/stores/my-users`, { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch users in store"
      );
    }
  },

  updateUserStatus: async (
    id: number
  ): Promise<ApiResponse<{ status: "ENABLE" | "DISABLE" }>> => {
    try {
      const response = await api.patch(`/users/${id}/status`);

      return response.data;
    } catch (error: any) {
      throw error.response?.data || "Failed to update user status";
    }
  },

  getUserLessons: async (
    id: number,
    params: CommonParams<{}>
  ): Promise<ApiResponse<UserLessonDto[]>> => {
    try {
      const paramsValue = {
        page: 1,
        isPagination: 1,
        limit: 12,
        orderBy: "id",
        order: "DESC",
        withDeleted: 0,
        ...params,
      };
      const response = await api.get(`/users/${id}/lesson`, {
        params: paramsValue,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data;
    }
  },

  getUserQuizzes: async (
    id: number,
    params: CommonParams<{}>
  ): Promise<ApiResponse<UserQuizDto[]>> => {
    try {
      const paramsValue = {
        page: 1,
        isPagination: 1,
        limit: 12,
        orderBy: "id",
        order: "DESC",
        withDeleted: 0,
        ...params,
      };
      const response = await api.get(`/users/${id}/quiz`, {
        params: paramsValue,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data;
    }
  },
  getSuperUsers: async (params: CommonParams<{ status?: COMMON_STATUS }>) => {
    const response = await api.get<ApiResponse<UserInStore[]>>(`/users/super`, {
      params,
    });
    return response.data;
  },
  updateUserPassword: async ({
    currentPassword,
    newPassword,
    confirmPassword,
  }: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<ApiResponse<null>> => {
    try {
      const response = await api.post(`/users/change-password`, {
        password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data;
    }
  },
};
